import {
  CheckCircleFilled,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import ApiQuestionnaireManager from 'services/api/ApiQuestionnaireManager';
import { QuestionnaireResponseDTO, QuestionnaireSchema } from 'services/api/models/QuestionnaireDTO';
import { colors, palette } from 'design';

const { Text } = Typography;

type Props = {
  response: QuestionnaireResponseDTO;
  schema: QuestionnaireSchema;
  /** When provided the star toggle is shown and highlights can be edited. */
  responseId?: number;
};

function resolveDisplayValue(
  q: QuestionnaireSchema['sections'][number]['questions'][number],
  answer: QuestionnaireResponseDTO['answers'][string],
  answers: QuestionnaireResponseDTO['answers'],
  t: ReturnType<typeof useTranslation>['t'],
): { displayValue: string; isEmpty: boolean } {
  const isEmpty =
    answer === undefined || answer === null || answer === '' || (Array.isArray(answer) && answer.length === 0);

  let displayValue: string;
  if (isEmpty) {
    displayValue = '—';
  } else if (Array.isArray(answer)) {
    displayValue = answer
      .map(v => {
        const choice = q.choices?.find(c => c.value === v);
        return choice ? choice.label : v;
      })
      .join(', ');
  } else if (q.type === 'select_year') {
    const year = Number(answer);
    const age = year > 0 && !isNaN(year) ? dayjs().year() - year : null;
    displayValue = age !== null ? `${answer} (${age} ans)` : String(answer);
  } else if (q.type === 'boolean') {
    displayValue = answer ? t('medicalProfile.questionnaire.yes') : t('medicalProfile.questionnaire.no');
  } else if (q.choices) {
    const choice = q.choices.find(c => c.value === answer);
    displayValue = choice ? choice.label : String(answer);
  } else {
    displayValue = String(answer);
  }

  if (q.id === 'weight_kg') {
    const heightCm = Number(answers['height_cm']);
    const weightKg = Number(answer);
    if (heightCm > 0 && weightKg > 0) {
      const heightM = heightCm / 100;
      const bmi = (weightKg / (heightM * heightM)).toFixed(1);
      displayValue = `${displayValue} — IMC : ${bmi}`;
    }
  }

  return { displayValue, isEmpty };
}

function QuestionRow({
  q,
  displayValue,
  isEmpty,
  isHighlighted,
  context = 'schema',
  onToggle,
}: {
  q: { id: string; label: string };
  displayValue: string;
  isEmpty: boolean;
  isHighlighted: boolean;
  /** 'highlight-list' = inside the highlights summary box; 'schema' = inside the full schema list */
  context?: 'highlight-list' | 'schema';
  onToggle?: () => void;
}) {
  const toggleIcon =
    context === 'highlight-list' ? (
      <MinusCircleOutlined />
    ) : isHighlighted ? (
      <ExclamationCircleFilled />
    ) : (
      <ExclamationCircleOutlined />
    );

  const toggleColor =
    context === 'highlight-list' ? colors.text.disabled : isHighlighted ? colors.dataViz.average : colors.text.disabled;

  const toggleAriaLabel =
    context === 'highlight-list' || isHighlighted ? 'Retirer des points à retenir' : 'Mettre en avant';

  return (
    <div
      style={{
        marginBottom: 10,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 8,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <Text style={{ color: colors.text.tertiary, fontSize: '0.85rem', display: 'block' }}>{q.label}</Text>
        <Text style={{ fontWeight: 500, color: isEmpty ? colors.text.tertiary : undefined }}>{displayValue}</Text>
      </div>
      {onToggle && (
        <button
          aria-label={toggleAriaLabel}
          onClick={onToggle}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: '2px 4px',
            marginTop: 2,
            flexShrink: 0,
            color: toggleColor,
            fontSize: '1rem',
            lineHeight: 1,
            transition: 'color 0.15s',
          }}
        >
          {toggleIcon}
        </button>
      )}
    </div>
  );
}

export default function QuestionnaireResponseViewer({ response, schema, responseId }: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { mutate: toggleHighlight } = useMutation({
    mutationFn: (questionId: string) => ApiQuestionnaireManager.toggleHighlight(responseId!, questionId),
    onSuccess: updatedIds => {
      queryClient.setQueryData<QuestionnaireResponseDTO | null>(
        ['questionnaire', 'responseForPatient', responseId],
        old => (old ? { ...old, highlighted_question_ids: updatedIds } : old),
      );
    },
  });

  // highlightedIds is derived entirely from the query cache (via the response prop).
  // The mutation's onSuccess patches the cache, which flows back here through the parent.
  const highlightedIds = response.highlighted_question_ids ?? [];

  const highlightedQuestions: Array<{
    q: QuestionnaireSchema['sections'][number]['questions'][number];
    displayValue: string;
    isEmpty: boolean;
  }> = [];

  for (const section of schema.sections) {
    for (const q of section.questions) {
      if (q.type === 'display' || q.type === 'separator' || q.type === 'info') continue;
      if (!highlightedIds.includes(q.id)) continue;
      const answer = response.answers[q.id];
      const resolved = resolveDisplayValue(q, answer, response.answers, t);
      highlightedQuestions.push({ q, ...resolved });
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 600, margin: '0 auto', width: '100%' }}>
      {response.reviewed_at && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
          <Tag style={{ alignSelf: 'flex-start', fontSize: '1rem' }}>
            <CheckCircleFilled style={{ color: colors.brand.accentStrong, marginRight: 8 }} />
            {t('medicalProfile.caregiver.reviewedBy', {
              date: dayjs(response.reviewed_at).format('DD/MM/YYYY'),
            })}
          </Tag>
        </div>
      )}

      {highlightedQuestions.length > 0 && (
        <div
          style={{
            background: palette.mint[50],
            border: `1.5px solid ${colors.brand.accentSubtle}`,
            borderRadius: 10,
            padding: '16px 20px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <ExclamationCircleFilled style={{ color: colors.dataViz.average, fontSize: '1rem' }} />
            <Text style={{ fontWeight: 600, color: colors.text.secondary, fontSize: '0.95rem' }}>
              {t('medicalProfile.caregiver.highlights.sectionTitle')}
            </Text>
          </div>
          {highlightedQuestions.map(({ q, displayValue, isEmpty }) => (
            <QuestionRow
              key={q.id}
              q={q}
              displayValue={displayValue}
              isEmpty={isEmpty}
              isHighlighted
              context="highlight-list"
              onToggle={responseId !== undefined ? () => toggleHighlight(q.id) : undefined}
            />
          ))}
        </div>
      )}

      {schema.sections.map(section => (
        <div key={section.id}>
          <h2
            style={{
              color: colors.brand.accentStrong,
              marginTop: 32,
              marginBottom: 32,
              paddingTop: 16,
              borderTop: `1px solid ${colors.brand.accentSubtle}`,
            }}
          >
            {section.label}
          </h2>
          {section.questions.map(q => {
            // Info alerts are patient-facing guidance only, not part of the response
            if (q.type === 'info') return null;
            if (q.type === 'display')
              return (
                <Text
                  key={q.id}
                  style={{
                    display: 'block',
                    color: colors.text.tertiary,
                    fontSize: '0.9rem',
                    fontStyle: 'italic',
                    marginBottom: 8,
                  }}
                >
                  {q.label}
                </Text>
              );
            if (q.type === 'separator')
              return <div key={q.id} style={{ borderTop: `1px solid ${colors.surface.page}`, margin: '16px 0' }} />;

            const answer = response.answers[q.id];
            const { displayValue, isEmpty } = resolveDisplayValue(q, answer, response.answers, t);

            return (
              <QuestionRow
                key={q.id}
                q={q}
                displayValue={displayValue}
                isEmpty={isEmpty}
                isHighlighted={highlightedIds.includes(q.id)}
                onToggle={responseId !== undefined ? () => toggleHighlight(q.id) : undefined}
              />
            );
          })}
        </div>
      ))}
    </div>
  );
}
